import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Star, Shield, Truck, Sparkles, CheckCircle2, Lock, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PayPalButton } from '../components/PayPalButton';
import { StripeButton } from '../components/StripeButton';
import { PaymentTrustBlock } from '../components/PaymentTrustBlock';
import { CheckoutChoiceModal } from '../components/CheckoutChoiceModal';
import { bundleData } from '../data/products';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd, productSchema, breadcrumbSchema } from '../hooks/useJsonLd';

export const BundlePage = () => {
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(bundleData.colors[0]);
  const [boyshortsSizeRaw, setBoyshortsSizeRaw] = useState('');
  const [boyshortsColorRaw, setBoyshortsColorRaw] = useState(bundleData.colors[0]);
  const [boyshortsTouched, setBoyshortsTouched] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [quantity] = useState(1);

  // Until the buyer explicitly picks for the boyshorts, mirror the faja choice
  const selectedBoyshortsSize = boyshortsTouched ? boyshortsSizeRaw : selectedSize;
  const selectedBoyshortsColor = boyshortsTouched ? boyshortsColorRaw : selectedColor;

  const pickBoyshortsSize = (size) => {
    setBoyshortsSizeRaw(size);
    setBoyshortsTouched(true);
  };
  const pickBoyshortsColor = (color) => {
    setBoyshortsColorRaw(color);
    setBoyshortsTouched(true);
  };

  useSEO({
    title: 'Snatched Bust To Thigh — Buy 2 Fajas, Get FREE Boyshorts',
    description: 'Buy 2 Fajas Colombianas (Full Body + Open-Bust) for $89.99 and get our seamless smoothing boyshorts FREE. Free US shipping + 30-day guarantee.',
    canonical: 'https://curveeforyou.com/'
  });

  useJsonLd([
    productSchema({
      name: 'The Snatched Duo + Free Boyshorts — CurveeForYou Bundle',
      description: 'Buy any 2 Fajas Colombianas — the Full Body Faja Colombiana with built-in bra plus the Open-Bust Tummy Control Faja — and get a high-quality Seamless Second-Skin Smoothing Boyshorts completely free.',
      image: [
        'https://customer-assets.emergentagent.com/job_item-landing/artifacts/ymvx3tpt_openart-14c4bd9b-a791-48cc-996b-d64c115a23b4.png',
        'https://curvluxe.com/cdn/shop/files/fajas-colombianas-tummy-control-shapewear-bodysuit-for-wedding-or-prom-9381302.jpg',
        'https://customer-assets.emergentagent.com/job_item-landing/artifacts/ocbok75k_the-seamless-second-skin-smoothing-boyshorts-1736402.jpg'
      ],
      sku: 'CFY-BUNDLE-DUO-001',
      price: bundleData.price,
      url: 'https://curveeforyou.com/',
      rating: bundleData.rating,
      reviewCount: bundleData.reviewCount
    }),
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Snatched Duo Bundle', url: '/' }
    ])
  ]);

  const handleBuyNow = () => {
    // If size not picked, scroll to size selector and bail
    if (!selectedSize) {
      document.getElementById('bundle-size-selection')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Boyshorts size defaults to the faja size unless user picks separately, so it's always present.
    // Open the payment choice modal — one tap to card OR PayPal, no scroll required.
    setCheckoutModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-rose-50/30 to-white pb-20 md:pb-0">

      {/* Top Banner */}
      <div className="bg-gradient-to-r from-[#d4af37] via-[#c49d2a] to-[#d4af37] text-[#5d1729] py-3 px-4">
        <div className="container mx-auto flex items-center justify-center gap-2 text-center">
          <Gift className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm md:text-lg font-bold uppercase tracking-wide">For A Limited Time — Snatched From Bust To Thigh: Buy 2 Fajas, Get FREE Boyshorts</span>
          <Gift className="w-5 h-5 flex-shrink-0" />
        </div>
      </div>

      {/* Navigation Menu Bar — mirrors Full Body Faja page for brand consistency */}
      <nav className="bg-white/95 backdrop-blur-md shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-center justify-between py-4">
            <Link to="/" aria-label="CurveeForYou home" data-testid="bundle-nav-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png"
                alt="CurveeForYou"
                className="h-20 md:h-24 w-auto"
              />
            </Link>

            {/* Desktop Menu — lean, conversion-focused */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8 ml-8 lg:ml-12">
              <Link to="/full-body-faja#reviews" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors" data-testid="bundle-nav-reviews-link">
                Reviews
              </Link>
              <Link to="/full-body-faja#guarantee" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors" data-testid="bundle-nav-guarantee-link">
                Guarantee
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-[#7d1f3d] font-medium transition-colors" data-testid="bundle-nav-about-link">
                About
              </Link>
              <Button
                onClick={handleBuyNow}
                data-testid="bundle-nav-buy-now-button"
                className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-6"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Buy Now
              </Button>
            </div>

            {/* Mobile Buy Now Button */}
            <Button
              onClick={handleBuyNow}
              data-testid="bundle-mobile-nav-buy-now"
              className="md:hidden bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-4 py-2 text-sm font-bold shadow-lg"
            >
              <ShoppingBag className="w-4 h-4 mr-1" />
              Buy Now
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero — above the fold: enlarged graphics + bundle details next to them */}
      <section className="container mx-auto px-4 sm:px-6 py-4 md:py-6 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">

          {/* Left: enlarged product visuals — each links to its individual product page */}
          <div className="lg:col-span-7 space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Link
                to="/full-body-faja"
                data-testid="bundle-hero-full-body-link"
                className="group bg-rose-50/30 rounded-2xl overflow-hidden shadow-2xl aspect-[3/4] relative block"
              >
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster={bundleData.images[0]}
                  className="w-full h-full object-cover"
                  aria-label="Full Body Faja product video"
                  data-testid="bundle-hero-full-body-video"
                >
                  <source src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/f7fd9ou4_Untitled%2086.mp4" type="video/mp4" />
                  <img
                    src={bundleData.images[0]}
                    alt="Full Body Faja Colombiana — view product"
                    className="w-full h-full object-cover object-top"
                  />
                </video>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-3 md:p-4">
                  <p className="text-white font-bold text-base md:text-lg">Full Body Faja</p>
                  <p className="text-white/80 text-xs md:text-sm">With built-in bra</p>
                </div>
              </Link>
              <Link
                to="/open-bust-faja"
                data-testid="bundle-hero-open-bust-link"
                className="group bg-rose-50/30 rounded-2xl overflow-hidden shadow-2xl aspect-[3/4] relative block"
              >
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster={bundleData.images[1]}
                  className="w-full h-full object-cover"
                  aria-label="Open-Bust Tummy Control Faja product video"
                  data-testid="bundle-hero-open-bust-video"
                >
                  <source src="https://customer-assets-m6fa6gv7.emergentagent.net/job_item-landing/artifacts/u3fe5wy9_Carla%20gif1.mp4" type="video/mp4" />
                  <img
                    src={bundleData.images[1]}
                    alt="Open-Bust Tummy Control Faja — view product"
                    className="w-full h-full object-cover object-top"
                  />
                </video>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-3 md:p-4">
                  <p className="text-white font-bold text-base md:text-lg">Open-Bust Faja</p>
                  <p className="text-white/80 text-xs md:text-sm">Strapless or your own bra</p>
                </div>
              </Link>
            </div>

            {/* Boyshorts full-width visual — included FREE with bundle */}
            <div className="relative bg-rose-50/30 rounded-2xl overflow-hidden shadow-xl max-w-md mx-auto aspect-[4/3] block">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_item-landing/artifacts/k2imn6dl_picture%203%20Boyshorts%20968677%20background%20-%20httpscurvluxe.comproductsseamless-second-skin-smoothing-boyshorts.jpg"
                alt="Seamless Second-Skin Smoothing Boyshorts — free with bundle"
                className="w-full h-full object-contain"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-3 flex items-center gap-3">
                <Badge className="bg-[#d4af37] text-[#5d1729] font-bold text-[10px] md:text-xs">FREE GIFT</Badge>
                <p className="text-white font-bold text-sm md:text-base">Seamless Second-Skin Smoothing Boyshorts</p>
              </div>
            </div>
          </div>

          {/* Right: bundle details, pricing, CTA */}
          <div className="lg:col-span-5 space-y-4">
            <Badge className="bg-[#d4af37] text-[#5d1729] font-bold px-3 py-1 text-xs uppercase tracking-wider">
              The Snatched Duo + Free Gift
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-[#7d1f3d] leading-[1.1]">
              {bundleData.name}
            </h1>
            <p className="text-gray-600 text-sm md:text-base">{bundleData.subtitle}</p>

            {/* Rating row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" />
                ))}
                <span className="ml-1 font-semibold text-gray-700">{bundleData.rating}/5</span>
                <span className="text-gray-500">({bundleData.reviewCount.toLocaleString()})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Truck className="w-4 h-4 text-[#7d1f3d]" /> Free US shipping
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 md:p-5">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-gray-700 text-sm">2 Fajas</span>
                <span className="text-gray-900">${bundleData.individualTotal.toFixed(2)}</span>
              </div>
              <div className="flex items-baseline justify-between mb-2 pb-2 border-b border-rose-200">
                <span className="text-green-700 text-sm font-semibold">+ High-Quality Seamless Boyshorts</span>
                <span className="text-green-700 font-bold">FREE</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-gray-900 font-bold">You pay:</span>
                <span className="text-4xl md:text-5xl font-bold text-[#7d1f3d]">${bundleData.price.toFixed(2)}</span>
              </div>
              <p className="text-right text-xs md:text-sm text-green-700 font-bold mt-1">
                Boyshorts gift included — no extra cost
              </p>
            </div>

            {/* CTA */}
            <Button
              onClick={handleBuyNow}
              data-testid="bundle-hero-cta"
              size="lg"
              className="w-full bg-[#7d1f3d] hover:bg-[#5d1729] text-white font-bold text-base px-7 py-6 shadow-lg"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Get the Bundle — ${bundleData.price.toFixed(2)}
            </Button>

            {/* What's in your bundle */}
            <div className="space-y-1.5 pt-1">
              <p className="font-bold text-gray-900 text-sm mb-1">What&apos;s in your bundle:</p>
              <div className="flex items-start gap-2.5 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#7d1f3d] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>1× Full Body Faja</strong> (with built-in bra)</p>
              </div>
              <div className="flex items-start gap-2.5 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#7d1f3d] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>1× Open-Bust Faja</strong> (pair with any bra)</p>
              </div>
              <div className="flex items-start gap-2.5 text-sm">
                <Gift className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>1× FREE Seamless Boyshorts</strong> <span className="text-green-700 font-bold">— yours free</span></p>
              </div>
              <div className="flex items-start gap-2.5 text-sm">
                <Shield className="w-4 h-4 text-[#7d1f3d] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>30-day money-back guarantee</strong></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout section — size, color, payment */}
      <section className="container mx-auto px-4 sm:px-6 pb-12 max-w-3xl">
        <div className="bg-white border border-rose-100 rounded-2xl shadow-lg p-6 md:p-8 space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#7d1f3d]">Choose your size &amp; color</h2>
            <p className="text-gray-600 text-sm">Pick the size and color for both fajas, then for your free boyshorts. They can be the same or different — your choice.</p>
          </div>

          {/* Faja Color */}
          <div className="space-y-2">
            <p className="font-semibold text-gray-900">Faja Color <span className="text-gray-500 text-xs font-normal">(both fajas)</span>: <span className="text-[#7d1f3d]">{selectedColor}</span></p>
            <div className="flex gap-3">
              {bundleData.colors.map((color) => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  data-testid={`bundle-color-${color}`}
                  className={`px-5 py-2 rounded-lg border-2 font-medium transition-all ${
                    selectedColor === color
                      ? 'bg-[#7d1f3d] text-white border-[#7d1f3d]'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-[#7d1f3d]'
                  }`}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>

          {/* Faja Size */}
          <div id="bundle-size-selection" className="space-y-2">
            <p className="font-semibold text-gray-900">Faja Size <span className="text-gray-500 text-xs font-normal">(both fajas)</span>:</p>
            <div className="grid grid-cols-4 gap-2">
              {bundleData.sizes.map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  data-testid={`bundle-size-${size}`}
                  className={`py-3 rounded-lg border-2 font-medium transition-all ${
                    selectedSize === size
                      ? 'bg-[#7d1f3d] text-white border-[#7d1f3d]'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-[#7d1f3d]'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Boyshorts (free gift) */}
          <div className="border-t border-rose-200 pt-5 space-y-4">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#d4af37]" />
              <p className="font-bold text-gray-900">Free Boyshorts <span className="text-green-700 font-bold">(included)</span></p>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Defaults to your faja size &amp; color — change them here if you want the boyshorts in a different size or color.
            </p>

            {/* Boyshorts Color */}
            <div className="space-y-2" data-testid="boyshorts-color-section">
              <p className="font-semibold text-gray-900">Boyshorts Color: <span className="text-[#d4af37]">{selectedBoyshortsColor}</span></p>
              <div className="flex gap-3">
                {bundleData.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => pickBoyshortsColor(color)}
                    data-testid={`boyshorts-color-${color}`}
                    className={`px-5 py-2 rounded-lg border-2 font-medium transition-all ${
                      selectedBoyshortsColor === color
                        ? 'bg-[#d4af37] text-[#5d1729] border-[#d4af37]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#d4af37]'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Boyshorts Size */}
            <div className="space-y-2" data-testid="boyshorts-size-section">
              <p className="font-semibold text-gray-900">Boyshorts Size:</p>
              <div className="grid grid-cols-4 gap-2">
                {bundleData.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => pickBoyshortsSize(size)}
                    data-testid={`boyshorts-size-${size}`}
                    className={`py-3 rounded-lg border-2 font-medium transition-all ${
                      selectedBoyshortsSize === size
                        ? 'bg-[#d4af37] text-[#5d1729] border-[#d4af37]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#d4af37]'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Checkout */}
          <div id="payment-section" className="space-y-3 pt-2">
            <StripeButton
              selectedSize={selectedSize}
              selectedColor={selectedColor}
              boyshortsSize={selectedBoyshortsSize}
              boyshortsColor={selectedBoyshortsColor}
              quantity={quantity}
              productData={bundleData}
            />

            <PaymentTrustBlock />

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">or pay with paypal</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <PayPalButton
              selectedSize={selectedSize}
              selectedColor={selectedColor}
              boyshortsSize={selectedBoyshortsSize}
              boyshortsColor={selectedBoyshortsColor}
              quantity={quantity}
              productData={bundleData}
            />

            <div className="flex items-center gap-2 text-sm text-gray-600 justify-center pt-2">
              <Lock className="w-4 h-4" />
              <span>Secure Checkout · 256-bit SSL</span>
            </div>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200">
            <div className="text-center">
              <Truck className="w-6 h-6 text-[#7d1f3d] mx-auto mb-1" />
              <p className="text-xs font-semibold">Ships Together</p>
            </div>
            <div className="text-center">
              <Shield className="w-6 h-6 text-[#7d1f3d] mx-auto mb-1" />
              <p className="text-xs font-semibold">30-Day Guarantee</p>
            </div>
            <div className="text-center">
              <Gift className="w-6 h-6 text-[#d4af37] mx-auto mb-1" />
              <p className="text-xs font-semibold">Free Boyshorts</p>
            </div>
          </div>
        </div>
      </section>


      {/* Site footer */}
      <footer className="bg-[#7d1f3d] text-white py-10 mt-16 mb-16 md:mb-0">
        <div className="container mx-auto px-6 text-center max-w-4xl">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-5 text-sm">
            <Link to="/faq" className="hover:text-[#d4af37] transition-colors">FAQ</Link>
            <Link to="/shipping" className="hover:text-[#d4af37] transition-colors">Shipping</Link>
            <Link to="/returns" className="hover:text-[#d4af37] transition-colors">Returns</Link>
            <Link to="/size-guide" className="hover:text-[#d4af37] transition-colors">Size Guide</Link>
            <Link to="/contact" className="hover:text-[#d4af37] transition-colors">Contact</Link>
            <Link to="/blog" className="hover:text-[#d4af37] transition-colors">Blog</Link>
          </div>
          <div className="text-sm opacity-90 mb-3 leading-relaxed">
            <p className="font-semibold mb-1">CurveeForYou</p>
            <p>1209 Mountain Road Place Northeast, Suite N</p>
            <p>Albuquerque, NM 87110, United States</p>
            <p className="mt-2">
              <a href="tel:+18002985173" className="hover:text-[#d4af37] transition-colors">Tel: 800 298 5173</a>
              {' · '}
              <a href="mailto:info@curveeforyou.com" className="hover:text-[#d4af37] transition-colors">info@curveeforyou.com</a>
            </p>
          </div>
          <p className="text-sm opacity-80">© 2025 CurveeForYou. All rights reserved.</p>
        </div>
      </footer>

      {/* Mobile sticky */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col leading-tight">
            <span className="text-xs text-green-700 font-semibold">+ FREE Boyshorts</span>
            <span className="text-lg font-bold text-[#7d1f3d]">${bundleData.price.toFixed(2)}</span>
          </div>
          <Button
            onClick={handleBuyNow}
            data-testid="bundle-mobile-sticky-buy-now"
            className="flex-1 bg-[#7d1f3d] hover:bg-[#5d1729] text-white font-bold text-base py-6 shadow-lg"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            GET THE BUNDLE
          </Button>
        </div>
      </div>

      {/* Checkout Choice Modal — one-tap Card or PayPal, no scroll required */}
      <CheckoutChoiceModal
        isOpen={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        selectedSize={selectedSize}
        selectedColor={selectedColor}
        boyshortsSize={selectedBoyshortsSize}
        boyshortsColor={selectedBoyshortsColor}
        quantity={quantity}
        productData={bundleData}
      />
    </div>
  );
};
